import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import {
  PeriodConfig,
  formatNumber,
  formatDate,
  formatMonth,
  COLORS,
  tooltipStyle,
} from '../types';

interface UsersTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = [
  'overview',
  'signups-daily',
  'signups-monthly',
  'signups-last30',
  'signups-hourly-5d',
  'cumulative-growth',
  'reg-types-daily',
  'reg-types',
  'platforms',
];

const UsersTab: React.FC<UsersTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  const overview = data['overview'];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dailySignups = useMemo(() => (data['signups-daily'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) })), [data]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthlySignups = useMemo(() => (data['signups-monthly'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) })), [data]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const signupsLast30 = useMemo(() => (data['signups-last30'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) })), [data]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hourly5dFormatted = useMemo(() => (data['signups-hourly-5d'] || []).map((d: any) => ({ ...d, label: `${d.date.slice(5)} ${d.hour}:00` })), [data]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { regTypesDailyFormatted, regTypeKeys } = useMemo(() => {
    const regTypesDaily = data['reg-types-daily'] || [];
    const formatted = regTypesDaily.map((d: any) => ({ date: formatDate(d.date), ...d.data }));
    const keys = regTypesDaily.length > 0 ? Array.from(new Set(regTypesDaily.flatMap((d: any) => Object.keys(d.data || {})))) as string[] : [];
    return { regTypesDailyFormatted: formatted, regTypeKeys: keys };
  }, [data]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cumulativeGrowth = useMemo(() => (data['cumulative-growth'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) })), [data]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Active Users" value={overview?.totalUsers || 0} />
        <StatCard
          title="Verified"
          value={overview?.verifiedUsers || 0}
          subtitle={`${overview ? ((overview.verifiedUsers / overview.totalUsers) * 100).toFixed(1) : 0}%`}
        />
        <StatCard title="Today" value={overview?.todaySignups || 0} />
        <StatCard title="Growth Rate" value={`${overview?.growthRate || 0}%`} subtitle="vs yesterday" />
      </div>

      <ChartCard title="Last 30 Days Signups">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={signupsLast30}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Daily Signups (Last 90 Days)">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={dailySignups}>
            <defs>
              <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF9C15" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#F42026" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="count" stroke="#FF9C15" fill="url(#userGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Signups Hourly (Last 5 Days)">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={hourly5dFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#fb923c" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Monthly Signups (from Jul 2025)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlySignups}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cumulative Growth">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cumulativeGrowth}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [formatNumber(v), 'Total']} />
              <Line type="monotone" dataKey="totalUsers" stroke="#F42026" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="Registration by Source (Last 30 Days)">
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={regTypesDailyFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            {regTypeKeys.map((key, i) => (
              <Bar key={key} dataKey={key} stackId="regType" fill={COLORS[i % COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Platform Distribution">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={data['platforms'] || []}
                dataKey="count"
                nameKey="platform"
                cx="50%"
                cy="50%"
                outerRadius={100}
                innerRadius={60}
              >
                {(data['platforms'] || []).map((_: any, i: number) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Registration Source (Total)">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={data['reg-types'] || []}
                dataKey="count"
                nameKey="type"
                cx="50%"
                cy="50%"
                outerRadius={100}
                innerRadius={60}
              >
                {(data['reg-types'] || []).map((_: any, i: number) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default UsersTab;
